// OPM backsolve iterative solver — PSD §4.2.5 (F2.3, F2.1). Engine layer (framework-free).
import { allocate } from './allocation';
import { fullyDilutedShares } from './fullyDiluted';
/**
 * Solve for total equity value V* such that the OPM-implied per-share value of
 * the anchor class equals the observed transaction price. Deterministic bracketed bisection.
 */
export function backsolve(inputs, bps) {
    const issues = [];
    const observed = inputs.equityValue.anchorPricePerShare;
    const anchorId = inputs.equityValue.anchorInstrumentId;
    const tol = inputs.solverTolerance;
    const maxIter = inputs.solverMaxIterations;
    const cap = inputs.capTableSnapshot;
    const anchor = anchorId ? cap.instruments.find((i) => i.id === anchorId) : null;
    if (!anchorId || !anchor) {
        issues.push({
            code: 'ANCHOR_INSTRUMENT_MISSING',
            message: `Anchor instrument "${anchorId ?? 'null'}" not found in cap table.`,
            instrumentId: anchorId,
        });
        return {
            V: NaN,
            diagnostics: {
                converged: false,
                iterations: 0,
                residualError: NaN,
                tolerance: tol,
                lowerBound: 0,
                upperBound: 0,
                multiAnchorCheck: null,
            },
            issues,
        };
    }
    if (!observed || observed <= 0 || !Number.isFinite(observed)) {
        issues.push({
            code: 'INVALID_ANCHOR_PRICE',
            message: `Anchor price per share must be > 0 (got ${observed}).`,
            instrumentId: anchorId,
        });
        return {
            V: NaN,
            diagnostics: {
                converged: false,
                iterations: 0,
                residualError: NaN,
                tolerance: tol,
                lowerBound: 0,
                upperBound: 0,
                multiAnchorCheck: null,
            },
            issues,
        };
    }
    const fdShares = fullyDilutedShares(cap);
    const anchorShares = Math.max(1, anchor.sharesOutstanding);
    const hi = observed * fdShares * 10; // generous upper bound
    const impliedPerShare = (V) => {
        if (!Number.isFinite(V) || V <= 0)
            return 0;
        const { results } = allocate(V, bps, inputs.assumptions, cap);
        const anchorValue = sumAnchorValue(results, anchorId);
        return anchorValue / anchorShares;
    };
    let lo = 0;
    let upper = hi;
    const fAt = (V) => impliedPerShare(V) - observed;
    let fLo = fAt(lo);
    let fHi = fAt(upper);
    // If bracket doesn't straddle, expand or report non-convergence.
    if (fLo * fHi > 0) {
        // Try expanding the upper bound a few times.
        for (let e = 0; e < 5 && fLo * fHi > 0; e++) {
            upper *= 10;
            fHi = fAt(upper);
        }
    }
    let mid = (lo + upper) / 2;
    let residual = NaN;
    let converged = false;
    let iter = 0;
    if (fLo * fHi <= 0) {
        for (iter = 1; iter <= maxIter; iter++) {
            mid = (lo + upper) / 2;
            const fMid = fAt(mid);
            residual = observed !== 0 ? Math.abs(fMid) / observed : /* v8 ignore next */ Math.abs(fMid);
            if (residual <= tol) {
                converged = true;
                break;
            }
            if (Math.sign(fMid) !== Math.sign(fLo)) {
                upper = mid;
                fHi = fMid;
            }
            else {
                lo = mid;
                fLo = fMid;
            }
            /* v8 ignore start */
            if (upper - lo < 1e-6) {
                mid = (lo + upper) / 2;
                residual = Math.abs(fAt(mid) - 0) / observed;
                converged = residual <= tol;
                break;
            }
            /* v8 ignore stop */
        }
        mid = (lo + upper) / 2;
    }
    else {
        // Could not bracket; report honest non-convergence.
        converged = false;
        residual = fLo * fHi > 0 ? NaN : /* v8 ignore next */ Math.abs(fAt(mid)) / observed;
    }
    // Non-convergence returns V = NaN (not the meaningless bracket midpoint).
    // NaN propagates visibly: allocate()'s !Number.isFinite guard yields an empty
    // allocation, which makes reconciled([], NaN) fail — so the failure surfaces
    // downstream instead of producing a plausible-but-garbage FMV.
    return {
        V: converged ? mid : NaN,
        diagnostics: {
            converged,
            iterations: Math.max(1, iter),
            residualError: Number.isFinite(residual) ? residual : 1,
            tolerance: tol,
            lowerBound: lo,
            upperBound: upper,
            multiAnchorCheck: null,
        },
        issues,
    };
}
/** Sum of an instrument's allocated value across all tranches. */
export function sumInstrumentValue(results, instrumentId) {
    let total = 0;
    for (const r of results) {
        for (const pc of r.perClass) {
            if (pc.instrumentId === instrumentId)
                total += pc.value;
        }
    }
    return total;
}
function sumAnchorValue(results, anchorId) {
    return sumInstrumentValue(results, anchorId);
}
/** Internal implied per-share for a given trial V (used by sensitivity/what-if). */
export function impliedPerShare(V, anchorId, bps, inputs) {
    const cap = inputs.capTableSnapshot;
    const anchor = cap.instruments.find((i) => i.id === anchorId);
    if (!anchor)
        return 0;
    const anchorShares = Math.max(1, anchor.sharesOutstanding);
    const { results } = allocate(V, bps, inputs.assumptions, cap);
    return sumInstrumentValue(results, anchorId) / anchorShares;
}
//# sourceMappingURL=backsolve.js.map